import { z } from "zod";
import type { FilterOption } from "@/components/designSystem/FilterSelect";

// 소속 분류 단일 출처: 플랫폼운영 / 운영사(CPO) / 고객사(CLIENT)
export const affiliationTypeSchema = z.enum(["MASTER", "CPO", "CLIENT"]);
export type AffiliationType = z.infer<typeof affiliationTypeSchema>;

export const AFFILIATION_TYPE_LABELS: Record<AffiliationType, string> = {
  MASTER: "플랫폼운영",
  CPO: "운영사",
  CLIENT: "고객사",
};

export const affiliationTypeLabel = (
  type: AffiliationType | null | undefined,
): string => (type ? AFFILIATION_TYPE_LABELS[type] : "-");

/** 계정·카드 등 "소속"을 고르는 화면의 선택지. 플랫폼운영은 소속 대상이 아니다 */
export const selectableAffiliationTypeSchema = affiliationTypeSchema.exclude([
  "MASTER",
]);
export type SelectableAffiliationType = z.infer<
  typeof selectableAffiliationTypeSchema
>;

export const SELECTABLE_AFFILIATION_TYPE_OPTIONS: FilterOption[] =
  selectableAffiliationTypeSchema.options.map((value) => ({
    value,
    label: AFFILIATION_TYPE_LABELS[value],
  }));

/**
 * 사업자 id · 운영사 id · 고객사 id 는 각 테이블이 자체 채번하므로 서로 다른 값이다.
 * 그런데 셋 다 number 라 자리를 바꿔 넣어도 타입이 통과하고, 우연히 존재하는 id 면
 * 서버도 400 을 주지 않고 다른 회사 데이터를 조회한다.
 *
 * 그래서 타입으로 갈라 컴파일 시점에 막는다. 브랜드 부여는 이 파일의 스키마와
 * 아래 변환 함수만 하고, 호출부에서 `as CpoId` 같은 캐스팅을 하지 않는다.
 */
export type BusinessId = number & { readonly __brand: "BusinessId" };
export type CpoId = number & { readonly __brand: "CpoId" };
export type ClientId = number & { readonly __brand: "ClientId" };

export const businessIdSchema = z.number().transform((v) => v as BusinessId);
export const cpoIdSchema = z.number().transform((v) => v as CpoId);
export const clientIdSchema = z.number().transform((v) => v as ClientId);

/** Select 값은 문자열이라 폼·필터에서 id 로 되돌릴 때 쓴다 */
export const toBusinessId = (value: string | number): BusinessId =>
  Number(value) as BusinessId;
export const toCpoId = (value: string | number): CpoId =>
  Number(value) as CpoId;
export const toClientId = (value: string | number): ClientId =>
  Number(value) as ClientId;

/** 충전소·충전기의 소유 운영사, 이력의 충전소 운영사, 매출 스코프 */
export const cpoInfoSchema = z.object({
  id: cpoIdSchema,
  name: z.string(),
});
export type CpoInfo = z.infer<typeof cpoInfoSchema>;

/**
 * 계약의 고객사. 계약 API 에서만 쓴다 — 카드·이력의 소유는 역할이 아니라 사업자다
 * (그쪽은 `businessRefSchema`).
 */
export const clientInfoSchema = z.object({
  id: clientIdSchema,
  name: z.string(),
});
export type ClientInfo = z.infer<typeof clientInfoSchema>;

/**
 * 사업자 참조(BE 의 `BusinessInfo`). 충전카드 소유 사업자, 이력의 카드 소유
 * 사업자, 충전기의 인증 허용 사업자가 쓴다.
 *
 * 카드가 운영사·고객사 역할이 아니라 사업자에 속하기 때문에 필요하다 — 운영사가 자기
 * 충전기에 쓰는 자사 카드는 고객사로 등록된 적이 없어 고객사 id 로는 표현되지 않는다.
 * 모양은 `CpoInfo`·`ClientInfo` 와 같지만 id 의 값 공간이 다르므로 타입을 가른다.
 */
export const businessRefSchema = z.object({
  id: businessIdSchema,
  name: z.string(),
});
export type BusinessRef = z.infer<typeof businessRefSchema>;

/**
 * 사업자가 가진 역할. 충전카드의 "소속 분류" 가 이 값이다.
 *
 * 한 사업자가 운영사이면서 다른 운영사의 고객사일 수 있어 응답은 배열이고,
 * 두 역할 필터에 모두 걸린다.
 */
export const ownerRoleSchema = selectableAffiliationTypeSchema;
export type OwnerRole = SelectableAffiliationType;

export const OWNER_ROLE_OPTIONS = SELECTABLE_AFFILIATION_TYPE_OPTIONS;

/** `운영사, 고객사`. 역할이 없으면 null — 빈 값 표기는 호출부가 고른다 */
export const formatOwnerRoles = (roles?: OwnerRole[] | null): string | null =>
  roles?.length
    ? roles.map((role) => AFFILIATION_TYPE_LABELS[role]).join(", ")
    : null;

/**
 * 계정의 소속. 계정 관리·CS·유지보수의 등록자/행위자가 공유한다.
 * MASTER 는 가리킬 역할 엔티티가 없어 id·name 이 모두 null 이며, 이때 소속 표기를 생략한다.
 */
export const affiliationInfoSchema = z.object({
  type: affiliationTypeSchema,
  type_label: z.string(),
  id: z.number().nullable(),
  name: z.string().nullable(),
});
export type AffiliationInfo = z.infer<typeof affiliationInfoSchema>;
